import { ReactNode, useMemo } from 'react'

import { useBalanceLabels, useBalanceSide } from '@features/balanceLedger'
import { BalanceSide } from '@features/balanceLedger/enums/balanceSide'

import { EMPTY_VALUE_PLACEHOLDER } from '../constants/emptyValuePlaceholder'
import { SUPPLIERS_BALANCE_COLUMN_WIDTHS } from '../constants/suppliersBalanceColumnWidths'
import { ColumnHeaderWithUnit } from '../elements/SuppliersBalanceTable/elements/ColumnHeaderWithUnit'
import { SupplierBalanceAmount } from '../elements/SuppliersBalanceTable/elements/SupplierBalanceAmount'
import { SupplierBalanceVariancePercentage } from '../elements/SuppliersBalanceTable/elements/SupplierBalanceVariancePercentage'
import { TruncatedTextCell } from '../elements/TruncatedTextCell'
import { SuppliersBalanceSortProperty } from '../enums/suppliersBalanceSortProperty'
import { SuppliersBalance, SuppliersBalanceTotals } from '../types/SuppliersBalance'

export type SuppliersBalanceColumnAlign = 'start' | 'end'

export interface SuppliersBalanceColumnDef {
  id: SuppliersBalanceSortProperty
  header: ReactNode
  sortable: boolean
  align?: SuppliersBalanceColumnAlign
  highlighted?: boolean
  width?: string
  Cell: (row: SuppliersBalance) => ReactNode
  Footer?: () => ReactNode
}

export const useSuppliersBalanceTableColumns = (
  currencyId?: string,
  totals?: SuppliersBalanceTotals,
): SuppliersBalanceColumnDef[] => {
  const { bt } = useBalanceLabels()
  const side = useBalanceSide()
  const currencyUnit = currencyId ? `(${currencyId})` : ''
  const accountColumnKey =
    side === BalanceSide.Customer ? 'table.column.customer_account' : 'table.column.supplier_account'

  return useMemo(
    (): SuppliersBalanceColumnDef[] => [
      {
        id: SuppliersBalanceSortProperty.AccountNumber,
        header: bt(accountColumnKey),
        sortable: true,
        width: SUPPLIERS_BALANCE_COLUMN_WIDTHS.accountNumber,
        Cell: (row) => <>{row.auxiliaryAccountNo || EMPTY_VALUE_PLACEHOLDER}</>,
      },
      {
        id: SuppliersBalanceSortProperty.Name,
        header: bt('table.column.label'),
        sortable: true,
        Cell: (row) => <TruncatedTextCell value={row.name} />,
      },
      {
        id: SuppliersBalanceSortProperty.Balance,
        header: <ColumnHeaderWithUnit label={bt('table.column.balance')} unit={currencyUnit} />,
        sortable: true,
        align: 'end',
        highlighted: true,
        width: SUPPLIERS_BALANCE_COLUMN_WIDTHS.amount,
        Cell: (row) => <SupplierBalanceAmount value={row.balance} />,
        Footer: () =>
          totals ? <SupplierBalanceAmount value={totals.balance} bold /> : <SupplierBalanceAmount renderDash bold />,
      },
      {
        id: SuppliersBalanceSortProperty.LastPeriod,
        header: <ColumnHeaderWithUnit label={bt('table.column.last_period')} unit={currencyUnit} />,
        sortable: true,
        align: 'end',
        width: SUPPLIERS_BALANCE_COLUMN_WIDTHS.amount,
        Cell: (row) => <SupplierBalanceAmount value={row.lastPeriod} />,
        Footer: () =>
          totals ? <SupplierBalanceAmount value={totals.lastPeriod} bold /> : <SupplierBalanceAmount renderDash bold />,
      },
      {
        id: SuppliersBalanceSortProperty.Variance,
        header: <ColumnHeaderWithUnit label={bt('table.column.variance')} unit={currencyUnit} />,
        sortable: true,
        align: 'end',
        width: SUPPLIERS_BALANCE_COLUMN_WIDTHS.amount,
        Cell: (row) => <SupplierBalanceAmount value={row.variance} />,
      },
      {
        id: SuppliersBalanceSortProperty.VariancePercentage,
        header: <ColumnHeaderWithUnit label={bt('table.column.variance')} unit="(%)" />,
        sortable: true,
        align: 'end',
        width: SUPPLIERS_BALANCE_COLUMN_WIDTHS.variancePercentage,
        Cell: (row) => <SupplierBalanceVariancePercentage value={row.variancePercentage} />,
      },
    ],
    [accountColumnKey, bt, currencyUnit, totals],
  )
}
